// Brand mark + wordmark. Inlined SVG so fill="currentColor" on the "KOin"
// text inherits color from the surrounding theme.
//
// Design notes (for future tweaks):
// - Circles use a 1px optical overlap (not exact tangent). Exact tangent
//   creates a hairline anti-aliasing seam at render — 1px overlap reads as a
//   single unified silhouette.
// - Coin baseline = KOin baseline = y:49. Everything sits on one typographic
//   line so the lockup reads as a word, not an icon-plus-text.
// - Colour: #0b8f5e is a deeper emerald than web-flat #10b981; avoids the
//   neon fintech look and holds up on both cream and dark backgrounds.
// - KOin uses aggressive negative tracking (-1.5) for a premium feel — the
//   same move Stripe/Linear/Vercel-style geometric wordmarks make.

type Props = { className?: string };

const FONT = "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const COIN = "#0b8f5e";

// Horizontal wordmark — nav, footer, auth cards, admin sidebar.
// Geometry: S (r=7) overlaps M (r=11) by 1px; M overlaps O (r=16) by 1px;
// KOin starts 8px right of the O's edge for a clean rhythmic gap.
export function Logo({ className }: Props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 170 56"
      className={className}
      role="img"
      aria-label="Smokoin"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <g fontFamily={FONT} fontWeight={800}>
        <circle cx="10" cy="42" r="7" fill={COIN} />
        <text x="10" y="45.5" textAnchor="middle" fill="#fff" fontSize="10" letterSpacing="-0.3">S</text>

        <circle cx="27" cy="38" r="11" fill={COIN} />
        <text x="27" y="42.5" textAnchor="middle" fill="#fff" fontSize="14" letterSpacing="-0.4">M</text>

        <circle cx="53" cy="33" r="16" fill={COIN} />
        <text x="53" y="39.5" textAnchor="middle" fill="#fff" fontSize="20" letterSpacing="-0.6">O</text>

        <text x="77" y="49" fill="currentColor" fontSize="32" letterSpacing="-1.5">KOin</text>
      </g>
    </svg>
  );
}

// Square mark — favicon, collapsed sidebar, app icon.
// Same geometry principles, scaled to sit inside 64×64 with symmetric padding.
export function LogoMark({ className }: Props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label="Smokoin"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <g fontFamily={FONT} fontWeight={800} fill="#fff">
        <circle cx="10" cy="50" r="6" fill={COIN} />
        <text x="10" y="53" textAnchor="middle" fontSize="9" letterSpacing="-0.3">S</text>

        <circle cx="24" cy="47" r="9" fill={COIN} />
        <text x="24" y="51" textAnchor="middle" fontSize="12" letterSpacing="-0.4">M</text>

        <circle cx="45" cy="43" r="13" fill={COIN} />
        <text x="45" y="48" textAnchor="middle" fontSize="17" letterSpacing="-0.6">O</text>
      </g>
    </svg>
  );
}
